import React, { useId } from 'react';
import { SanityImage } from 'sanity-image';

import { SanityImageType } from '../../graphql-fragments/SanityImage';

type SanityImageProps = React.ComponentProps<typeof SanityImage>;
import { useSanityConfigData } from '../../utils/sanity';
import { clsx, hash_cyrb53 } from '../../utils/utils';
import * as styles from './Image.module.scss';

export type BasicImage = {
  url: string;
  alt?: string;
};

export type ImageType = SanityImageType | BasicImage;

interface BaseImageProps
  extends Omit<SanityImageProps, 'id'>,
    Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'width' | 'height'> {
  className?: string;
}

type ImageProps = BaseImageProps &
  (
    | ({
        image: SanityImageType;
        cover?: boolean;
        eagerLoading?: boolean;
      } & (
        | {
            dimensions: [
              [number | undefined, number | undefined],
              ...Array<
                | [number, number | undefined, number | undefined]
                | [number, SanityImageType, number | undefined, number | undefined]
              >,
            ];
            width?: never;
            height?: never;
          }
        | {
            dimensions?: never;
            width?: number;
            height?: number;
          }
      ))
    | {
        image: BasicImage;
        cover?: never;
        eagerLoading?: never;
        dimensions?: never;
      }
    | {
        image: ImageType;
        cover?: never;
        eagerLoading?: never;
        dimensions?: never;
      }
  );

function Image({
  image,
  cover,
  eagerLoading,
  className,
  dimensions,
  ...restProps
}: ImageProps): React.ReactElement {
  const sanityConfigData = useSanityConfigData();
  const id = useId();

  function isSanityImage(image: BasicImage | SanityImageType): image is SanityImageType {
    return 'asset' in image && !!image.asset._id;
  }

  function getImageClass(index: number) {
    // Add the 'c' to ensure it starts with a letter
    return 'c' + hash_cyrb53(id, { format: 'hex' }).slice(0, 4) + '-' + index;
  }

  if (isSanityImage(image)) {
    let cleanDimensions: Array<[number, SanityImageType, number | undefined, number | undefined]>;
    if (!dimensions) {
      // If we don't have dimensions, convert width and height
      // to dimensions (if existing) and clear them from restProps.
      // Fall back to a sensible 1920px ceiling when the callsite
      // provides nothing: sanity-image would otherwise request the
      // full original asset, which is often many megapixels.
      const fallbackWidth =
        restProps.width == null && restProps.height == null ? 1920 : restProps.width;
      cleanDimensions = [[0, image, fallbackWidth, restProps.height]];
      delete restProps.width;
      delete restProps.height;
    } else {
      // If we have dimensions, clean them into an easier to use format where
      // every entry has the minWidth (the first element gets 0) and image in index 1
      cleanDimensions = [[0, image, dimensions[0][0], dimensions[0][1]]];
      let currentImage = image;
      for (const dimension of dimensions.slice(1)) {
        if (dimension.length === 4) {
          currentImage = dimension[1];
          cleanDimensions.push([dimension[0] || 0, currentImage, dimension[2], dimension[3]]);
        } else {
          cleanDimensions.push([dimension[0] || 0, currentImage, dimension[1], dimension[2]]);
        }
      }
    }
    return (
      <>
        <style
          dangerouslySetInnerHTML={{
            __html: cleanDimensions
              .map(
                (dimension, i) =>
                  `@media (min-width: ${dimension[0]}px) {${cleanDimensions
                    .map((_, j) => `.${getImageClass(j)} {display: ${i == j ? 'block' : 'none'}}`)
                    .join(' ')}}`,
              )
              .join('\n'),
          }}
        ></style>
        {cleanDimensions.map((dimension, i) => {
          const [, dimensionImage, width, height] = dimension;
          // Without a `sizes` attr, browsers assume 100vw when picking from
          // srcset, so they pull the 1920w variant for images that display
          // at a few hundred px. Default sizes to the target width in px —
          // callers can override by passing their own `sizes` in restProps.
          const defaultSizes = width != null ? `${width}px` : undefined;
          return (
            <SanityImage
              key={i}
              className={clsx(styles.image, className, getImageClass(i))}
              {...sanityConfigData}
              id={dimensionImage.asset._id}
              hotspot={dimensionImage.hotspot}
              crop={dimensionImage.crop}
              preview={dimensionImage.asset.metadata?.lqip}
              alt={dimensionImage.asset.altText || ''}
              loading={eagerLoading ? 'eager' : 'lazy'}
              mode={cover ? 'cover' : 'contain'}
              width={width}
              height={height}
              sizes={defaultSizes}
              {...restProps}
            />
          );
        })}
      </>
    );
  }

  return <img src={image.url} alt={image.alt || ''} className={className} loading="lazy" />;
}

export default Image;
